// Sound effects.
//
// The settings panel has had SFX and music volumes since before there was any
// audio; this is what finally reads them.
//
// Deliberately small: one `Audio` element per sound, cloned to play, no Web
// Audio graph. A card game fires a handful of short cues, not a mix, and an
// AudioContext brings a suspend/resume dance with autoplay policy that is more
// machinery than the feature is worth.

/** The cues the game can make, and the file each comes from. */
const SOUNDS = {
  cardHover: 'sfx_card_hover',
  cardDraw: 'sfx_card_draw',
  cardPlay: 'sfx_card_play',
  spell: 'sfx_card_spell_activate',
  buttonHover: 'sfx_main_button_hover',
  buttonClick: 'sfx_main_button_click',
  roundStart: 'sfx_round_start',
  showdown: 'sfx-showdown_start',
} as const;

export type Cue = keyof typeof SOUNDS;

/**
 * How loud each cue is relative to the others.
 *
 * Levelled by hand because the files are not: a hover that fires on every card
 * the pointer crosses has to sit far below a spell that fires once.
 */
const LEVEL: Record<Cue, number> = {
  cardHover: 0.25,
  cardDraw: 0.5,
  cardPlay: 0.7,
  spell: 0.8,
  buttonHover: 0.3,
  buttonClick: 0.6,
  roundStart: 0.7,
  showdown: 0.9,
};

/** A cue that fires on pointer movement must not stack into a buzz. */
const THROTTLE: Partial<Record<Cue, number>> = {
  cardHover: 90,
  buttonHover: 90,
};

export class Audio_ {
  private readonly buffers = new Map<Cue, HTMLAudioElement>();
  private readonly lastPlayed = new Map<Cue, number>();
  /** 0..1, from settings. Muted is volume 0 rather than a second flag. */
  private volume = 0;
  private loaded = false;

  /** Loads the files. Cheap: eight short clips, and failure is silent. */
  load(): void {
    if (this.loaded) return;
    this.loaded = true;
    for (const [cue, file] of Object.entries(SOUNDS) as [Cue, string][]) {
      const el = new window.Audio(`/sfx/${file}.ogg`);
      el.preload = 'auto';
      this.buffers.set(cue, el);
    }
  }

  setVolume(volume: number, muted: boolean): void {
    this.volume = muted ? 0 : Math.max(0, Math.min(1, volume));
  }

  play(cue: Cue): void {
    if (this.volume <= 0) return;
    const source = this.buffers.get(cue);
    if (!source) return;

    const gap = THROTTLE[cue];
    if (gap !== undefined) {
      const last = this.lastPlayed.get(cue) ?? 0;
      if (Date.now() - last < gap) return;
      this.lastPlayed.set(cue, Date.now());
    }

    // Cloned so a second play does not cut the first off mid-cue, which is
    // what re-triggering one element does.
    const voice = source.cloneNode() as HTMLAudioElement;
    voice.volume = this.volume * LEVEL[cue];
    // A browser that has not seen a gesture yet refuses to play, and says so by
    // rejecting. That is expected on the first frames, not an error.
    void voice.play().catch(() => {});
  }
}

/** One player for the page. */
export const audio = new Audio_();
